import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Button, Card, Col, Row, Statistic, Table, Tag } from 'antd';

import RealTimeActivityFeed from '@/components/RealTimeActivityFeed';
import SystemHealthCard from '@/components/SystemHealthCard';
import SuperAdminAPI from '@/services/SuperAdminAPI';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toList = (value) => (Array.isArray(value) ? value : []);

const Dashboard = ({ embedded = false } = {}) => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [systemHealth, setSystemHealth] = useState({});
  const [tenantHealth, setTenantHealth] = useState({ averageScore: 0, count: 0, items: [] });
  const [activity, setActivity] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewPayload, analyticsPayload, healthPayload, tenantHealthPayload] = await Promise.all([
        SuperAdminAPI.getDashboardOverview(),
        SuperAdminAPI.getAnalytics('30d'),
        SuperAdminAPI.getSystemHealth(),
        SuperAdminAPI.getTenantHealthScores({ limit: 12, order: 'asc' }),
      ]);

      setOverview(overviewPayload || {});
      setAnalytics(analyticsPayload || {});
      setSystemHealth(healthPayload || {});
      setTenantHealth({
        averageScore: toNumber(tenantHealthPayload?.averageScore, 0),
        count: toNumber(tenantHealthPayload?.count, 0),
        items: toList(tenantHealthPayload?.items),
      });
      setActivity(toList(overviewPayload?.recentEvents || overviewPayload?.activity || []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const tenantRows = useMemo(
    () =>
      toList(tenantHealth?.items).map((item, idx) => ({
        key: item?._id || item?.tenantId || `${idx}`,
        name: item?.name || item?.tenantName || item?.slug || 'Unknown tenant',
        slug: item?.slug || 'tenant',
        score: toNumber(item?.score || item?.healthScore, 0),
        status: String(item?.status || 'unknown'),
      })),
    [tenantHealth]
  );

  const dashboardBody = (
    <div style={{ margin: '24px 16px 0', overflow: 'initial' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Total Tenants"
              value={toNumber(overview?.totalTenants, tenantRows.length)}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Active Users" value={toNumber(overview?.activeUsers, 0)} loading={loading} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={toNumber(analytics?.monthlyRevenue, 0)}
              precision={2}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 2 }}>
        <Col xs={24} lg={14}>
          <Card title="Tenant Health Rankings">
            <Table
              rowKey="key"
              loading={loading}
              dataSource={tenantRows}
              pagination={{ pageSize: 8 }}
              columns={[
                { title: 'Tenant', dataIndex: 'name', key: 'name' },
                { title: 'Slug', dataIndex: 'slug', key: 'slug' },
                {
                  title: 'Health Score',
                  dataIndex: 'score',
                  key: 'score',
                  render: (score) => (
                    <Tag color={score >= 80 ? 'green' : score >= 60 ? 'gold' : 'red'}>{score}</Tag>
                  ),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (value) => <Tag>{String(value || 'unknown')}</Tag>,
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <SystemHealthCard systemHealth={systemHealth} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 2 }}>
        <Col xs={24}>
          <RealTimeActivityFeed events={activity} />
        </Col>
      </Row>

      {tenantRows.length === 0 && !loading ? (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="Dashboard is live"
          description="No tenant metrics were returned yet, but the dashboard shell and data bindings are active."
        />
      ) : null}

      <div style={{ marginTop: 16 }}>
        <Button onClick={loadData} loading={loading}>
          Refresh Dashboard
        </Button>
      </div>
    </div>
  );

  if (embedded) return dashboardBody;
  return <main style={{ minHeight: '100vh' }}>{dashboardBody}</main>;
};

export default Dashboard;

